import posthog from 'posthog-js';

import type { PostHogConfig } from 'posthog-js';
import { COOKIE_NAMES } from './cookieConsent';
import { dropNoisyExceptions } from './posthogErrorFilters';
import {
  startNavigationTypeTracking,
  tagNavigationType,
} from './posthogNavigation';

/**
 * PostHog — client-side error tracking + Core Web Vitals + product analytics.
 *
 * Driven by the Jun-24 tech sync: we had no visibility into client-side
 * errors / performance when a customer reported "it's broken". PostHog is
 * the ONLY error-tracking system in this app, so exception capture here is
 * reliability telemetry, not a nice-to-have.
 *
 * FAIL-SOFT, mirroring `lib/gtm.ts` / `lib/ga4.ts`:
 *   - No `NEXT_PUBLIC_POSTHOG_KEY` → `initPostHog()` is a no-op.
 *   - Server-side (no `window`)  → no-op.
 *
 * CONSENT MODEL — two tiers:
 *
 *   1. Reliability telemetry (ALWAYS on, no consent required):
 *      - `$exception` capture (unhandled errors + promise rejections)
 *      - Core Web Vitals
 *      These are strictly-necessary technical signals. To stay
 *      privacy-clean we run them with `persistence: 'memory'` until
 *      consent, so NO tracking cookie/localStorage id is written for
 *      users who haven't opted in.
 *
 *   2. Product analytics (ONLY after analytics consent):
 *      - autocapture (clicks / inputs), pageviews, session replay, and a
 *        persistent `localStorage+cookie` id.
 *
 * Why a reload on consent change instead of runtime `set_config`:
 * posthog-js attaches autocapture / replay listeners at init via
 * `startIfEnabled`, so toggling them at runtime is unreliable. A reload
 * is cheap (pages are usually cached) and guarantees a clean capture
 * posture for the new consent state.
 *
 * @module lib/posthog
 */

const DEFAULT_UI_HOST = 'https://us.posthog.com';

/**
 * PostHog consumes its per-type exception token before `before_send`, so the
 * default 10-token bucket lets nine discarded TypeErrors starve the next real
 * TypeError. Doubling the bucket and refill preserves the default 100-second
 * full-refill horizon, leaves a default-sized reserve after that demonstrated
 * burst, and still throttles a sustained >0.2/second same-type loop.
 */
const EXCEPTION_RATE_LIMITS = {
  __exceptionRateLimiterBucketSize: 20,
  __exceptionRateLimiterRefillRate: 2,
} satisfies PostHogConfig['error_tracking'];

/**
 * Read the configured PostHog project key. Returns the empty string when
 * unset so callers can branch without a separate env-existence helper —
 * same shape as `getGtmId()`.
 */
export function getPostHogKey(): string {
  return process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '';
}

function getUiHost(): string {
  return process.env.NEXT_PUBLIC_POSTHOG_HOST || DEFAULT_UI_HOST;
}

/** True when the user has granted analytics consent via the cookie banner. */
export function hasAnalyticsConsent(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(/;\s*/)
    .some((c) => c === `${COOKIE_NAMES.ANALYTICS}=true`);
}

let initialized = false;

/**
 * Initialise PostHog once on the client. Idempotent. No-op on the server
 * or when the project key is unset.
 *
 * Exception + Web Vitals capture starts immediately for every visitor;
 * product analytics is enabled only when analytics consent is already
 * present at load. A later consent change reloads the page so the new
 * posture is applied cleanly (see module doc).
 */
export function initPostHog(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  if (!getPostHogKey()) return;

  initialized = true;

  // Before `posthog.init` on purpose — see `startNavigationTypeTracking`.
  startNavigationTypeTracking();

  const consented = hasAnalyticsConsent();

  posthog.init(getPostHogKey(), {
    // Ingestion is reverse-proxied through `/ingest` (see next.config.ts)
    // so ad blockers don't drop telemetry. `ui_host` keeps toolbar links
    // pointing at the real PostHog app.
    api_host: '/ingest',
    ui_host: getUiHost(),
    defaults: '2026-06-25',
    // Drop third-party / browser-noise exceptions before they leave the
    // browser (GTM tags, WebView bridge, extensions, benign network / script
    // errors) so they don't drown out real app errors in the tracker.
    // See `lib/posthogErrorFilters.ts`.
    // Array form: dropping third-party noise and labelling how a route
    // change happened are separate concerns, kept in separate modules.
    before_send: [dropNoisyExceptions, tagNavigationType],
    // Only ever materialise person profiles for users we explicitly
    // identify (signed-in + consented). Anonymous + non-consented traffic
    // stays event-only.
    person_profiles: 'identified_only',

    // ── Tier 1: reliability telemetry — ALWAYS on ──
    error_tracking: EXCEPTION_RATE_LIMITS,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // React logs every error-boundary-caught error via `console.error`,
      // so capturing console errors picks up all our `error.tsx` /
      // `<SafeBoundary>` boundary crashes too — without editing 28
      // boundary files — plus any explicit `console.error(err)`.
      capture_console_errors: true,
    },
    capture_performance: { web_vitals: true },

    // ── Tier 2: product analytics — gated on analytics consent ──
    autocapture: consented,
    capture_pageview: consented ? 'history_change' : false,
    capture_pageleave: consented,
    capture_dead_clicks: consented,
    disable_session_recording: !consented,
    // No persistent identifier until consent — keeps non-consented error
    // capture cookieless (GDPR-clean).
    persistence: consented ? 'localStorage+cookie' : 'memory',
  });

  // A consent flip can't be applied to autocapture/replay listeners at
  // runtime reliably, so reload to re-init with the new posture.
  window.addEventListener('cookie-consent-changed', () => {
    if (hasAnalyticsConsent() !== consented) {
      window.location.reload();
    }
  });
}

/**
 * Browser-side activation events we mirror into PostHog from the marketing
 * GTM / Meta pipeline (`lib/gtm.ts`) so the product funnel (Landing → Signup
 * → Dash → first_search → Deep Research) is complete in PostHog too.
 *
 * Server-side journey events (`property_searched`, `deep_research_started`,
 * …) are emitted from `lib/productAnalytics.ts` instead — this is only for
 * the handful of client-fired activation signals.
 */
export type ProductActivationEvent =
  | 'signup_completed'
  | 'first_search'
  | 'onboarding_profile_submitted'
  | 'onboarding_profile_skipped'
  | 'demo_portfolio_opened'
  | 'deal_pin_clicked';

/**
 * Capture a client-side activation event in PostHog. Fail-soft, mirroring
 * `pushEvent` / `InAppBrowserSplash`: SSR / unset key / a throwing capture
 * are all silent no-ops. Fire event-only signals here (no PII in props) —
 * PostHog only materialises a person profile for identified + consented
 * users, so a pre-consent capture stays anonymous.
 */
export function captureProductEvent(
  event: ProductActivationEvent,
  props?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  if (!getPostHogKey()) return;
  try {
    posthog.capture(event, props);
  } catch {
    // Analytics is best-effort — a capture failure must never break the UI.
  }
}
